import { DEFAULT_COLOR, DEFAULT_FONT_SIZE, FONT_FAMILY, TABLE_BORDER_COLOR, TABLE_HEADER_BACKGROUND } from './houseStyle';

// Zeilen/Spalten in Tabellen (HtmlBlock) ändern, direkt im HTML: neue Zeilen/Zellen sind Kopien der
// vorhandenen (alle Styles bleiben), nur ohne Text. Feste Breiten (520px-Tabellen aus email_bb) bleiben in der Summe gleich.

// leere Kopie einer Zelle: Hülle (z.B. <span style="font…">) behalten, Text raus
export function emptyCellCopy(cell) {
    const copy = cell.cloneNode(false);
    const styled = cell.querySelector('span[style]');
    const holder = styled ? styled.cloneNode(false) : cell.ownerDocument.createElement('span');
    holder.innerHTML = '&nbsp;';
    copy.appendChild(holder);
    return copy;
}

function isHeaderRow(row) {
    return Array.from(row.cells).some(cell => /background/i.test(cell.getAttribute('style') || '') || cell.hasAttribute('bgcolor'));
}

export function addRow(row, position) {
    // graue Kopfzeile ist kein gutes Vorbild für Datenzeilen
    const model = isHeaderRow(row) && row.nextElementSibling ? row.nextElementSibling : row;
    const copy = model.cloneNode(false);
    Array.from(model.cells).forEach(cell => copy.appendChild(emptyCellCopy(cell)));
    row.parentNode.insertBefore(copy, position === 'before' ? row : row.nextSibling);
    return copy;
}

export function deleteRow(row) {
    const next = row.nextElementSibling || row.previousElementSibling;
    row.remove();
    return next;
}

// Raster: grid[zeile][spalte] = Zelle (colspan/rowspan aufgelöst)
function buildGrid(table) {
    const grid = [];
    Array.from(table.rows).forEach((row, r) => {
        grid[r] = grid[r] || [];
        let c = 0;
        Array.from(row.cells).forEach(cell => {
            while (grid[r][c]) {
                c++;
            }
            for (let dr = 0; dr < (cell.rowSpan || 1); dr++) {
                grid[r + dr] = grid[r + dr] || [];
                for (let dc = 0; dc < (cell.colSpan || 1); dc++) {
                    grid[r + dr][c + dc] = cell;
                }
            }
            c += cell.colSpan || 1;
        });
    });
    return grid;
}

function columnOf(grid, cell) {
    const r = cell.parentElement.rowIndex;
    return grid[r].indexOf(cell);
}

function widthOf(cell) {
    const fromStyle = (cell.getAttribute('style') || '').match(/(?:^|;)\s*width\s*:\s*(\d+(?:\.\d+)?)px/i);
    if (fromStyle) {
        return parseFloat(fromStyle[1]);
    }
    const attribute = cell.getAttribute('width');
    return attribute && /^\d+$/.test(attribute) ? parseInt(attribute, 10) : null;
}

function setWidth(cell, width) {
    const rounded = Math.round(width);
    if (cell.hasAttribute('width')) {
        cell.setAttribute('width', String(rounded));
    }
    const style = cell.getAttribute('style') || '';
    if (/(?:^|;)\s*width\s*:/i.test(style)) {
        cell.setAttribute('style', style.replace(/((?:^|;)\s*width\s*:\s*)[^;]*/i, `$1${rounded}px`));
    }
}

// neue Spalte links/rechts der Zelle; gibt die neue Zelle in der Zeile der Ausgangszelle zurück
export function addColumn(cell, position) {
    const table = cell.closest('table');
    const grid = buildGrid(table);
    const c = columnOf(grid, cell);
    const target = position === 'before' ? c : c + (cell.colSpan || 1) - 1;
    const handled = new Set();
    let created = null;

    grid.forEach(gridRow => {
        const current = gridRow[target];
        if (!current || handled.has(current)) {
            return;
        }
        handled.add(current);
        const neighbour = position === 'before' ? gridRow[target - 1] : gridRow[target + 1];
        if (neighbour === current || (current.colSpan || 1) > 1) {
            // Zelle über mehrere Spalten (z.B. graue Kopfzeile mit colspan) → wird einfach breiter
            current.colSpan = (current.colSpan || 1) + 1;
            return;
        }
        const copy = emptyCellCopy(current);
        copy.removeAttribute('colspan');
        copy.removeAttribute('rowspan');
        const width = (current.colSpan || 1) === 1 ? widthOf(current) : null;
        if (width) {
            // Breite teilen, damit die Tabelle (520px) nicht wächst
            setWidth(current, width - Math.floor(width / 2));
            setWidth(copy, Math.floor(width / 2));
        }
        current.parentNode.insertBefore(copy, position === 'before' ? current : current.nextSibling);
        if (current === cell) {
            created = copy;
        }
    });
    return created;
}

// Spalte der Zelle löschen; Breite geht an die Nachbarspalte. Gibt eine Zelle zurück, in die der Cursor kann.
export function deleteColumn(cell) {
    const table = cell.closest('table');
    const grid = buildGrid(table);
    const c = columnOf(grid, cell);
    const columns = Math.max(...grid.map(gridRow => gridRow.length));
    if (columns <= 1) {
        return cell;
    }
    const handled = new Set();
    let focus = null;

    grid.forEach(gridRow => {
        const current = gridRow[c];
        if (!current || handled.has(current)) {
            return;
        }
        handled.add(current);
        if ((current.colSpan || 1) > 1) {
            current.colSpan -= 1;
            return;
        }
        const neighbour = gridRow[c - 1] || gridRow[c + 1];
        const width = widthOf(current);
        if (neighbour && neighbour !== current && width && widthOf(neighbour) && (neighbour.colSpan || 1) === 1) {
            setWidth(neighbour, widthOf(neighbour) + width);
        }
        if (current === cell) {
            focus = neighbour || null;
        }
        current.remove();
    });
    return focus;
}

// "Tabelle einfügen": gleiche Bauart wie die Baustein-Tabellen (tableAddition.twig/td.twig) – 520px, Linien zwischen
// den Zeilen, graue Kopfzeile. Danach ist sie eine Tabelle wie jede andere.
export function newTableHtml(rows = 3, columns = 3) {
    const total = 520;
    const widths = Array.from({length: columns}, (value, index) =>
        Math.floor(total / columns) + (index < total % columns ? 1 : 0));
    const font = `font-family:${FONT_FAMILY};font-size:${DEFAULT_FONT_SIZE};color:${DEFAULT_COLOR}`;
    const cell = (width, header) => `<td width="${width}" valign="top" style="width:${width}px;vertical-align:top;`
        + `border-top:1px solid ${TABLE_BORDER_COLOR};padding:3pt 6pt;${header ? `background:${TABLE_HEADER_BACKGROUND};` : ''}">`
        + `<span style="${font}${header ? ';font-weight:bold' : ''}">&nbsp;</span></td>`;
    const row = header => `<tr>${widths.map(width => cell(width, header)).join('')}</tr>`;
    return `<table border="0" cellspacing="0" cellpadding="0" width="${total}" style="width:${total}px;border-collapse:collapse;`
        + `border-bottom:1px solid ${TABLE_BORDER_COLOR}"><tbody>${row(true)}${Array.from({length: rows - 1}, () => row(false)).join('')}</tbody></table>`;
}

// Zellenfarbe: background der Zelle setzen bzw. entfernen
export function setCellBackground(cell, color) {
    const style = (cell.getAttribute('style') || '').split(';')
        .map(item => item.trim())
        .filter(item => item && !/^background(-color)?\s*:/i.test(item));
    if (color) {
        style.push(`background:${color}`);
    }
    cell.removeAttribute('bgcolor');
    if (style.length) {
        cell.setAttribute('style', style.join(';'));
    } else {
        cell.removeAttribute('style');
    }
}
